import { useEffect, useState, useCallback } from 'react';
import { api } from '../lib/api.js';

// ── Types ────────────────────────────────────────────────────────────────────

interface PpcAction {
  id: string;
  accountId: string;
  workflow: string;
  actionType: string;
  entityType: string;
  entityId: string | null;
  targetCampaign: string | null;
  targetCampaignName: string | null;
  targetAdGroup: string | null;
  targetAdGroupName: string | null;
  keywordText: string | null;
  searchTerm: string | null;
  currentValue: string | null;
  suggestedValue: string | null;
  modifiedValue: string | null;
  details: Record<string, unknown>;
  aiReasoning: string | null;
  impressions: number | null;
  clicks: number | null;
  spend: number | null;
  sales: number | null;
  orders: number | null;
  acos: number | null;
  daysOfData: number | null;
  status: string;
  approvedAt: string | null;
  executedAt: string | null;
  errorMessage: string | null;
  batchId: string | null;
  createdAt: string;
}

interface ActionsResponse {
  actions: PpcAction[];
  total: number;
  limit: number;
  offset: number;
}

interface Account {
  id: string;
  name: string;
}

// ── Styles ───────────────────────────────────────────────────────────────────

const STATUS_STYLES: Record<string, string> = {
  pending: 'bg-yellow-100 text-yellow-700',
  approved: 'bg-blue-100 text-blue-700',
  applied: 'bg-green-100 text-green-700',
  failed: 'bg-red-100 text-red-700',
  rejected: 'bg-gray-100 text-gray-500',
  executing: 'bg-blue-100 text-blue-700',
};

const WORKFLOW_STYLES: Record<string, string> = {
  harvest: 'bg-purple-100 text-purple-700',
  bid_opt: 'bg-blue-100 text-blue-700',
  budget: 'bg-green-100 text-green-700',
  expand: 'bg-orange-100 text-orange-700',
  cannibalization: 'bg-red-100 text-red-700',
  campaign_create: 'bg-teal-100 text-teal-700',
};

const WORKFLOW_LABELS: Record<string, string> = {
  harvest: 'Harvest',
  bid_opt: 'Bid Opt',
  budget: 'Budget',
  expand: 'Expand',
  cannibalization: 'Cannibal.',
  campaign_create: 'Create',
};

const ACTION_LABELS: Record<string, string> = {
  create_keyword: 'Create Keyword',
  add_negative: 'Add Negative',
  campaign_negative: 'Campaign Neg.',
  adjust_bid: 'Adjust Bid',
  adjust_budget: 'Adjust Budget',
  add_keyword: 'Add Keyword',
  pause_campaign: 'Pause Campaign',
  new_campaign: 'New Campaign',
};

const PAGE_SIZE = 25;

// ── Component ────────────────────────────────────────────────────────────────

export function PpcActions() {
  const [actions, setActions] = useState<PpcAction[]>([]);
  const [total, setTotal] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [filterWorkflow, setFilterWorkflow] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expandedReasoning, setExpandedReasoning] = useState<Set<string>>(new Set());
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<Set<string>>(new Set());

  // Load accounts on mount
  useEffect(() => {
    api.get<Account[]>('/accounts').then((accts) => {
      const active = accts.filter((a: Account & { is_active?: number }) => a.is_active !== 0);
      if (active.length > 0) setSelectedAccount(active[0].id);
      else setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const loadActions = useCallback(async () => {
    if (!selectedAccount) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filterWorkflow) params.set('workflow', filterWorkflow);
      if (filterStatus) params.set('status', filterStatus);
      params.set('limit', String(PAGE_SIZE));
      params.set('offset', String(offset));

      const qs = params.toString();
      const resp = await api.get<ActionsResponse>(
        `/ppc/actions/${selectedAccount}${qs ? '?' + qs : ''}`,
      );
      setActions(resp.actions);
      setTotal(resp.total);
      setSelected(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load actions');
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, filterWorkflow, filterStatus, offset]);

  useEffect(() => { loadActions(); }, [loadActions]);

  // Reset offset when filters change
  useEffect(() => { setOffset(0); }, [filterWorkflow, filterStatus, selectedAccount]);

  // ── Action handlers ──────────────────────────────────────────────────────

  function toggleSelect(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function selectAllPending() {
    const pendingIds = actions.filter((a) => a.status === 'pending').map((a) => a.id);
    setSelected((prev) => {
      const allSelected = pendingIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(pendingIds);
    });
  }

  async function handleBulkApprove() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkLoading(true);
    try {
      await api.post('/ppc/actions/bulk-approve', { actionIds: ids });
      await loadActions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk approve failed');
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleBulkReject() {
    const ids = Array.from(selected);
    if (ids.length === 0) return;
    setBulkLoading(true);
    try {
      await api.post('/ppc/actions/bulk-reject', { actionIds: ids });
      await loadActions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Bulk reject failed');
    } finally {
      setBulkLoading(false);
    }
  }

  async function handleSingleApprove(id: string) {
    setActionLoading((prev) => new Set(prev).add(id));
    try {
      await api.post(`/ppc/actions/${id}/approve`);
      await loadActions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Approve failed');
    } finally {
      setActionLoading((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  async function handleSingleReject(id: string) {
    setActionLoading((prev) => new Set(prev).add(id));
    try {
      await api.post(`/ppc/actions/${id}/reject`);
      await loadActions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reject failed');
    } finally {
      setActionLoading((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  async function handleModifyValue(id: string) {
    const newVal = editingValues[id];
    if (newVal === undefined) return;
    setActionLoading((prev) => new Set(prev).add(id));
    try {
      await api.put(`/ppc/actions/${id}`, { modifiedValue: newVal });
      await loadActions();
      setEditingValues((prev) => { const n = { ...prev }; delete n[id]; return n; });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Update failed');
    } finally {
      setActionLoading((prev) => { const n = new Set(prev); n.delete(id); return n; });
    }
  }

  function toggleReasoning(id: string) {
    setExpandedReasoning((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // ── Pagination ───────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const pendingCount = actions.filter((a) => a.status === 'pending').length;
  const allPendingSelected = pendingCount > 0 && actions.filter((a) => a.status === 'pending').every((a) => selected.has(a.id));

  if (loading && actions.length === 0) return <LoadingSkeleton />;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold">PPC Action Queue</h2>
          <p className="text-sm text-gray-500">Review, approve, or reject AI-generated PPC suggestions.</p>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <select
          value={filterWorkflow}
          onChange={(e) => setFilterWorkflow(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Workflows</option>
          <option value="harvest">Harvest</option>
          <option value="bid_opt">Bid Optimization</option>
          <option value="budget">Budget</option>
          <option value="expand">Expand</option>
          <option value="cannibalization">Cannibalization</option>
          <option value="campaign_create">Campaign Create</option>
        </select>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="applied">Applied</option>
          <option value="rejected">Rejected</option>
          <option value="failed">Failed</option>
        </select>

        <span className="text-xs text-gray-500">{total} total actions</span>
      </div>

      {/* Bulk actions bar */}
      {pendingCount > 0 && (
        <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={allPendingSelected}
              onChange={selectAllPending}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
            Select all pending ({pendingCount})
          </label>
          <div className="h-4 w-px bg-gray-300" />
          <button
            onClick={handleBulkApprove}
            disabled={selected.size === 0 || bulkLoading}
            className="rounded-lg bg-green-600 px-3 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            Approve Selected ({selected.size})
          </button>
          <button
            onClick={handleBulkReject}
            disabled={selected.size === 0 || bulkLoading}
            className="rounded-lg bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            Reject Selected ({selected.size})
          </button>
        </div>
      )}

      {/* Actions table */}
      {actions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center">
          <p className="text-sm font-medium text-gray-900">No actions found</p>
          <p className="mt-1 text-xs text-gray-500">
            {filterWorkflow || filterStatus
              ? 'Try adjusting your filters.'
              : 'Run a PPC workflow to generate suggestions.'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50">
                <th className="px-3 py-2 text-left">
                  <span className="sr-only">Select</span>
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Workflow</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Action</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">Campaign / Keyword</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Current</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Suggested</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Performance</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500">Status</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {actions.map((action) => (
                <ActionRow
                  key={action.id}
                  action={action}
                  isSelected={selected.has(action.id)}
                  onToggleSelect={() => toggleSelect(action.id)}
                  isLoading={actionLoading.has(action.id)}
                  onApprove={() => handleSingleApprove(action.id)}
                  onReject={() => handleSingleReject(action.id)}
                  isReasoningExpanded={expandedReasoning.has(action.id)}
                  onToggleReasoning={() => toggleReasoning(action.id)}
                  editValue={editingValues[action.id]}
                  onEditValueChange={(val) => setEditingValues((p) => ({ ...p, [action.id]: val }))}
                  onSaveValue={() => handleModifyValue(action.id)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">
            Showing {offset + 1}-{Math.min(offset + PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const pageStart = Math.max(0, currentPage - 3);
              const page = pageStart + i + 1;
              if (page > totalPages) return null;
              return (
                <button
                  key={page}
                  onClick={() => setOffset((page - 1) * PAGE_SIZE)}
                  className={`rounded-lg px-3 py-1 text-xs font-medium ${
                    page === currentPage
                      ? 'bg-brand-600 text-white'
                      : 'border border-gray-200 text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => setOffset(Math.min((totalPages - 1) * PAGE_SIZE, offset + PAGE_SIZE))}
              disabled={currentPage >= totalPages}
              className="rounded-lg border border-gray-200 px-3 py-1 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Action Row ───────────────────────────────────────────────────────────────

function ActionRow({
  action,
  isSelected,
  onToggleSelect,
  isLoading,
  onApprove,
  onReject,
  isReasoningExpanded,
  onToggleReasoning,
  editValue,
  onEditValueChange,
  onSaveValue,
}: {
  action: PpcAction;
  isSelected: boolean;
  onToggleSelect: () => void;
  isLoading: boolean;
  onApprove: () => void;
  onReject: () => void;
  isReasoningExpanded: boolean;
  onToggleReasoning: () => void;
  editValue: string | undefined;
  onEditValueChange: (val: string) => void;
  onSaveValue: () => void;
}) {
  const isPending = action.status === 'pending';
  const isEditing = editValue !== undefined;

  return (
    <>
      <tr className={isSelected ? 'bg-brand-50' : ''}>
        <td className="px-3 py-2">
          {isPending && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={onToggleSelect}
              className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
            />
          )}
        </td>
        <td className="px-3 py-2">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${WORKFLOW_STYLES[action.workflow] ?? 'bg-gray-100 text-gray-500'}`}>
            {WORKFLOW_LABELS[action.workflow] ?? action.workflow}
          </span>
        </td>
        <td className="px-3 py-2 text-gray-700">{ACTION_LABELS[action.actionType] ?? action.actionType}</td>
        <td className="px-3 py-2 max-w-[220px]">
          {action.targetCampaignName && (
            <p className="truncate text-xs text-gray-500" title={action.targetCampaignName}>{action.targetCampaignName}</p>
          )}
          <p className="truncate font-medium text-gray-900" title={action.keywordText ?? action.searchTerm ?? ''}>
            {action.keywordText ?? action.searchTerm ?? '--'}
          </p>
        </td>
        <td className="px-3 py-2 text-right text-gray-500">{action.currentValue ?? '--'}</td>
        <td className="px-3 py-2 text-right">
          {isPending && isEditing ? (
            <div className="flex items-center justify-end gap-1">
              <input
                type="text"
                value={editValue}
                onChange={(e) => onEditValueChange(e.target.value)}
                className="w-20 rounded border border-gray-300 px-2 py-0.5 text-right text-xs focus:border-brand-500 focus:outline-none"
              />
              <button
                onClick={onSaveValue}
                disabled={isLoading}
                className="rounded bg-brand-600 px-1.5 py-0.5 text-xs text-white hover:bg-brand-700 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          ) : (
            <span
              className={`font-medium text-gray-900 ${isPending ? 'cursor-pointer underline decoration-dashed' : ''}`}
              onClick={isPending ? () => onEditValueChange(action.modifiedValue ?? action.suggestedValue ?? '') : undefined}
              title={isPending ? 'Click to edit' : undefined}
            >
              {action.modifiedValue ?? action.suggestedValue ?? '--'}
            </span>
          )}
        </td>
        <td className="px-3 py-2 text-right">
          {action.spend !== null ? (
            <div className="text-xs text-gray-500">
              <span>{action.clicks ?? 0}cl</span>
              <span className="mx-1">/</span>
              <span>${(action.spend ?? 0).toFixed(2)}</span>
              {action.acos !== null && (
                <>
                  <span className="mx-1">/</span>
                  <span className={action.acos > 0.3 ? 'text-red-600' : action.acos > 0.2 ? 'text-yellow-600' : 'text-green-600'}>
                    {(action.acos * 100).toFixed(0)}%
                  </span>
                </>
              )}
            </div>
          ) : (
            <span className="text-xs text-gray-400">--</span>
          )}
        </td>
        <td className="px-3 py-2 text-center">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLES[action.status] ?? 'bg-gray-100 text-gray-500'}`}>
            {action.status}
          </span>
        </td>
        <td className="px-3 py-2 text-right">
          <div className="flex items-center justify-end gap-1">
            {action.aiReasoning && (
              <button
                onClick={onToggleReasoning}
                className="rounded border border-gray-200 px-1.5 py-0.5 text-xs text-gray-500 hover:bg-gray-50"
                title="AI reasoning"
              >
                AI
              </button>
            )}
            {isPending && (
              <>
                <button
                  onClick={onApprove}
                  disabled={isLoading}
                  className="rounded border border-green-200 px-2 py-0.5 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
                >
                  Approve
                </button>
                <button
                  onClick={onReject}
                  disabled={isLoading}
                  className="rounded border border-red-200 px-2 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  Reject
                </button>
              </>
            )}
          </div>
        </td>
      </tr>
      {/* Expanded AI reasoning */}
      {isReasoningExpanded && action.aiReasoning && (
        <tr>
          <td colSpan={9} className="bg-gray-50 px-6 py-3">
            <p className="text-xs font-medium text-gray-500">AI Reasoning:</p>
            <p className="mt-1 text-sm text-gray-700 whitespace-pre-wrap">{action.aiReasoning}</p>
            {action.executedAt && (
              <p className="mt-2 text-xs text-gray-400">
                Executed: {new Date(action.executedAt).toLocaleString()}
              </p>
            )}
            {action.errorMessage && (
              <p className="mt-1 text-xs text-red-600">Error: {action.errorMessage}</p>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

// ── Loading skeleton ─────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="flex items-center justify-between">
        <div>
          <div className="h-5 w-40 rounded bg-gray-200" />
          <div className="mt-2 h-4 w-72 rounded bg-gray-200" />
        </div>
      </div>
      <div className="flex gap-3">
        <div className="h-8 w-36 rounded-lg bg-gray-200" />
        <div className="h-8 w-36 rounded-lg bg-gray-200" />
      </div>
      <div className="h-10 rounded-lg bg-gray-200" />
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="h-12 rounded bg-gray-200" />
      ))}
    </div>
  );
}
